import { formatToHumanReadable } from "@/lib/format-data";

import { PaymentMedium } from "@/types/enums/payment";
import {
  PaymentMediumKind,
  PaymentMedium as PaymentMediumType,
} from "@/types/payment";

/**
 * Payment mediums that flow through a payment processor (Rainforest/Stripe).
 * The underlying payment method on these can be CARD or ACH.
 */
const PROCESSOR_PAYMENT_MEDIUMS: Set<PaymentMedium> = new Set([
  PaymentMedium.POS,
  PaymentMedium.VIRTUAL_TERMINAL,
  PaymentMedium.PAYMENT_LINK,
  PaymentMedium.PAYMENT_PLAN,
  PaymentMedium.SUBSCRIPTION,
  PaymentMedium.SAVED_PAYMENT_METHOD,
  PaymentMedium.MEMBERSHIP,
  PaymentMedium.EMBEDDED_TERMINAL,
]);

/**
 * Payment medium group names for L1/L2 filtering
 */
export const PAYMENT_MEDIUM_GROUPS = {
  CARD: "Card Transactions",
  EXTERNAL: "External Settlements",
} as const;

/**
 * Check if a payment medium flows through a payment processor (Rainforest/Stripe).
 * Returns true for processor mediums regardless of whether the underlying
 * payment method is CARD or ACH. Custom mediums return false.
 */
export function isPaymentMediumProcessor(
  paymentMedium: string | PaymentMedium | null | undefined,
): boolean {
  if (!paymentMedium) return false;

  if (!Object.values(PaymentMedium).includes(paymentMedium as PaymentMedium)) {
    return false;
  }

  return PROCESSOR_PAYMENT_MEDIUMS.has(paymentMedium as PaymentMedium);
}

/**
 * Get the group name for a payment medium.
 *
 * @param paymentMedium - The payment medium value (string)
 * @returns The group name (Card Transactions or External Settlements)
 */
export function getPaymentMediumGroup(paymentMedium: string): string {
  return isPaymentMediumProcessor(paymentMedium)
    ? PAYMENT_MEDIUM_GROUPS.CARD
    : PAYMENT_MEDIUM_GROUPS.EXTERNAL;
}

/**
 * Grouped filter option type for payment mediums
 */
export interface GroupedFilterOption {
  label: string;
  value: string;
  group: string;
}

/**
 * Convert a list of payment mediums to grouped filter options.
 * Groups payment mediums into "Card Transactions" and "External Settlements".
 *
 * @param paymentMediums - Array of payment medium objects from the API
 * @returns Array of filter options with group property for L1/L2 filtering
 */
export function getGroupedPaymentMediumOptions(
  paymentMediums: PaymentMediumType[] | null | undefined,
): GroupedFilterOption[] {
  if (!paymentMediums) return [];

  return paymentMediums
    .filter((pm) => !pm.isArchived)
    .map((pm) => ({
      label: formatToHumanReadable(pm.name),
      value: pm.name,
      group: getPaymentMediumGroup(pm.name),
    }))
    .sort((a, b) => {
      // Sort by group first (Card before External), then by label
      if (a.group !== b.group) {
        return a.group === PAYMENT_MEDIUM_GROUPS.CARD ? -1 : 1;
      }
      return a.label.localeCompare(b.label);
    });
}

export const PAYMENT_KIND_LABELS: Record<PaymentMediumKind, string> = {
  CARD: "Card",
  CASH: "Cash",
  CHECK: "Check",
  FINANCING: "Financing",
  REBATE: "Rebate",
  PEER_TO_PEER: "Peer-to-peer",
  OUTSIDE_CARD: "Card outside Decoda",
  VOUCHER: "Voucher",
  STORE_CREDIT: "Store credit",
  OTHER: "Other",
};

/** Kinds staff can give a payment type they add; the rest are built in. */
export const CUSTOM_PAYMENT_KINDS: PaymentMediumKind[] = [
  "FINANCING",
  "REBATE",
  "PEER_TO_PEER",
  "OUTSIDE_CARD",
  "VOUCHER",
  "CASH",
  "CHECK",
  "OTHER",
];

/** Built-in types are stored as enum names (CARE_CREDIT); custom ones as typed. */
export function paymentTypeLabel(medium: { name: string; isSystem: boolean }) {
  return medium.isSystem ? formatToHumanReadable(medium.name) : medium.name;
}
